import { Check, Minus } from "@phosphor-icons/react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import * as React from "react"

import {
  usePermissionGate,
  type PermissionGateProps,
} from "~/components/reusable/PermissionTooltip"
import { cn } from "~/utils/helpers"

/**
 * Toggle a binary value or select rows. For status-like signals (flag/approve/tag) use IndicatorCheckbox.
 * Pass `requires` to permission-gate the checkbox — disables itself and renders its own
 * permission tooltip.
 * Canonical intent: {@link ../design-system/intents.ts CHECKBOX_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    indeterminate?: boolean
    iconClassName?: string
  } & PermissionGateProps
>(
  (
    {
      className,
      indeterminate,
      iconClassName,
      disabled,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const checkedValue = indeterminate ? "indeterminate" : props.checked // Without doing this, the CheckboxPrimitive.Indicator will not render when checked=false.
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
    })

    const checkbox = (
      // TODO: replace bg-button-primary with a dedicated checkbox-on color token.
      // Using the primary button token as a stop-gap to get the purpleheart hue here.
      <CheckboxPrimitive.Root
        ref={ref}
        disabled={disabled || blocked}
        className={cn(
          "peer h-5 w-5 shrink-0 cursor-pointer rounded-sm border-none transition-[background-color,box-shadow,color]",
          "bg-component shadow-borders-base hover:bg-component-hover data-[force-hover]:bg-component-hover",
          "focus-visible:outline-none focus-visible:shadow-borders-interactive-with-shadow!",
          "data-[force-focus]:outline-none data-[force-focus]:shadow-borders-interactive-with-shadow!",
          "data-[state=indeterminate]:bg-interactive data-[state=indeterminate]:text-fg-on-color",
          "data-[state=indeterminate]:shadow-borders-interactive-with-shadow",
          "data-[state=indeterminate]:hover:bg-interactive-hover data-[state=indeterminate]:data-[force-hover]:bg-interactive-hover",
          "data-[state=checked]:bg-interactive data-[state=checked]:text-fg-on-color",
          "data-[state=checked]:hover:bg-interactive-hover data-[state=checked]:data-[force-hover]:bg-interactive-hover",
          "data-[state=checked]:shadow-borders-interactive-with-shadow",
          "disabled:cursor-not-allowed! disabled:text-fg-disabled! disabled:bg-component! disabled:shadow-borders-base!",

          className,
        )}
        {...props}
        checked={checkedValue}
      >
        {/* Mounted so toggles transition; a mount animation would replay on every virtualized row. */}
        <CheckboxPrimitive.Indicator
          forceMount
          className={cn(
            "group/indicator relative flex items-center justify-center text-current",
            "scale-50 transition-[scale] duration-100 ease-overlay",
            "data-[state=checked]:scale-100 data-[state=indeterminate]:scale-100",
          )}
        >
          {/* Both glyphs stay rendered so the one on screen is the one that fades out. */}
          <Check
            size="16"
            className={cn(
              "stroke-[2.5] opacity-0 transition-opacity duration-100 ease-overlay",
              "group-data-[state=checked]/indicator:opacity-100",
              iconClassName,
            )}
          />
          <Minus
            size="16"
            className={cn(
              "absolute inset-0 m-auto stroke-[2.5] opacity-0 transition-opacity duration-100 ease-overlay",
              "group-data-[state=indeterminate]/indicator:opacity-100",
              iconClassName,
            )}
          />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    )

    return gate(checkbox)
  },
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

/**
 * Status-signal checkbox variant — looks filled even when unchecked, carries semantic colour.
 * Canonical intent: {@link ../design-system/intents.ts INDICATOR_CHECKBOX_INTENT}.
 */
const IndicatorCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    indeterminate?: boolean
    iconClassName?: string
    variant?: "red" | "green" | "blue"
  } & PermissionGateProps
>(({ className, indeterminate, iconClassName, variant = "red", ...props }, ref) => {
  const getVariantClasses = (variant: "red" | "green" | "blue") => {
    switch (variant) {
      case "red":
        return {
          checked:
            "data-[state=checked]:bg-indicator-checkbox-red data-[state=checked]:hover:bg-indicator-checkbox-red-hover data-[state=checked]:data-[force-hover]:bg-indicator-checkbox-red-hover data-[state=checked]:text-fg-on-red-500",
          indeterminate:
            "data-[state=indeterminate]:bg-indicator-checkbox-red hover:bg-indicator-checkbox-red-hover data-[state=indeterminate]:data-[force-hover]:bg-indicator-checkbox-red-hover data-[state=indeterminate]:text-fg-on-red-500",
          unchecked:
            "bg-indicator-checkbox-red hover:bg-indicator-checkbox-red-hover data-[state=unchecked]:data-[force-hover]:bg-indicator-checkbox-red-hover",
        }
      case "green":
        return {
          checked:
            "data-[state=checked]:bg-indicator-checkbox-green data-[state=checked]:hover:bg-indicator-checkbox-green-hover data-[state=checked]:data-[force-hover]:bg-indicator-checkbox-green-hover data-[state=checked]:text-fg-on-green-500",
          indeterminate:
            "data-[state=indeterminate]:bg-indicator-checkbox-green hover:bg-indicator-checkbox-green-hover data-[state=indeterminate]:data-[force-hover]:bg-indicator-checkbox-green-hover data-[state=indeterminate]:text-fg-on-green-500",
          unchecked:
            "bg-indicator-checkbox-green hover:bg-indicator-checkbox-green-hover data-[state=unchecked]:data-[force-hover]:bg-indicator-checkbox-green-hover",
        }
      case "blue":
        return {
          checked:
            "data-[state=checked]:bg-indicator-checkbox-blue data-[state=checked]:hover:bg-indicator-checkbox-blue-hover data-[state=checked]:data-[force-hover]:bg-indicator-checkbox-blue-hover data-[state=checked]:text-fg-on-blue-500",
          indeterminate:
            "data-[state=indeterminate]:bg-indicator-checkbox-blue hover:bg-indicator-checkbox-blue-hover data-[state=indeterminate]:data-[force-hover]:bg-indicator-checkbox-blue-hover data-[state=indeterminate]:text-fg-on-blue-500",
          unchecked:
            "bg-indicator-checkbox-blue hover:bg-indicator-checkbox-blue-hover data-[state=unchecked]:data-[force-hover]:bg-indicator-checkbox-blue-hover",
        }
    }
  }

  const variantClasses = getVariantClasses(variant)

  return (
    <Checkbox
      ref={ref}
      className={cn(
        variantClasses.unchecked,
        variantClasses.checked,
        variantClasses.indeterminate,
        "shadow-indicator-checkbox-background",
        "data-[state=checked]:shadow-indicator-checkbox-background",
        "data-[state=indeterminate]:shadow-indicator-checkbox-background",
        "data-[state=unchecked]:shadow-indicator-checkbox-background",
        className,
      )}
      iconClassName={cn("text-fg-on-color", iconClassName)}
      indeterminate={indeterminate}
      {...props}
    />
  )
})
IndicatorCheckbox.displayName = "IndicatorCheckbox"

export { Checkbox, IndicatorCheckbox }
